/**
 * The rental flow's step machine, as pure functions.
 *
 * The flow keeps ONE piece of step state (`selectedStep`) and derives what to
 * render from it every render. Two things about that derivation are easy to
 * get wrong, and both have bitten:
 *
 * 1. "questions" only exists when the form has renderable questions. A listing
 *    with none must render "configure" even if state still says "questions"
 *    (e.g. the guest count dropped and took the last question with it).
 * 2. "basketChoice" is reachable on EVERY listing — it must not be gated on the
 *    form having questions, or a question-less rental (the common case) could
 *    never reach it. It IS gated on the configure state still being complete:
 *    the pool-cap clamp runs asynchronously and can drop the start time or
 *    lower the units after the buyer has already moved on. `canContinue` is
 *    the flow's definition of "complete" (ticket, date, start, enough
 *    fractions, start fits the units); `buildItem()` returns null on the
 *    first three of those, so it can never be null while `canContinue`
 *    holds. Rendering the choice step on an incomplete configure would leave
 *    two buttons that do nothing — or, under `directCheckout`, a
 *    "Continuing to checkout…" placeholder that never continues.
 *
 * The flow mirrors the resolved step back into `selectedStep` during render
 * (the same reset it already did for case 1), so state and render agree.
 */

export type RentalSelectedStep = "configure" | "questions" | "basketChoice";

export function resolveRentalStep(input: {
  selectedStep: RentalSelectedStep;
  /** The user form has at least one renderable question. */
  showQuestionsStep: boolean;
  /** The configure state is complete — the same gate as the footer's Continue. */
  canContinue: boolean;
}): RentalSelectedStep {
  const { selectedStep, showQuestionsStep, canContinue } = input;
  if (selectedStep === "basketChoice") {
    if (canContinue) return "basketChoice";
    return showQuestionsStep ? "questions" : "configure";
  }
  if (selectedStep === "questions") {
    return showQuestionsStep ? "questions" : "configure";
  }
  return "configure";
}

/** Where Back goes from the basket-choice step. */
export function rentalStepBeforeBasketChoice(
  showQuestionsStep: boolean,
): Exclude<RentalSelectedStep, "basketChoice"> {
  return showQuestionsStep ? "questions" : "configure";
}
